import { ErrorBoundary } from "@sentry/react";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { setParameters, setSpecificParameter } from "../../actions";
import "../../assets/styles/component/filters.scss";
import { GEO_DATE_FILTER_OPTIONS, DEFAULT_GEO_REPORTING_PARAMETERS, ALL_OPTION_NO_SPACE, MIN_SELECTABLE_DATE } from "../../constants";
import { FilterContext } from "../../context";
import { currentDefaultFiltersSet, defaultFiltersSet } from "../../utils/utilityFunctions";
import { ErrorMsg } from "../AppMessages";
import { FiltersLayout } from "../Layouts";
import { DateFilter, FilterBtns, MultiSelectFilter } from "./common";
import { useQuery } from "@tanstack/react-query";
import { getGeoReportingFilters } from "../../api/geoReportingApi";
import { closeFilterOptions, closeMarketOptions } from "./subs/helpers";
import { eventTracking, MixpanelEvents } from "../../utils/userTracking";

interface Props {
    filterBtns: Array<{ id: string; navigateTo: string; name: string }>;
    hideFilters?: boolean;
    lastDataRefresh: string;
}

const isStaleParam = (val: string) =>
    !val || (val.startsWith("All") && val !== ALL_OPTION_NO_SPACE) || val === "undefined";

export const GeoReportingFilters = withRouter((props: RouteComponentProps & Props) => {
    const { history, filterBtns, hideFilters, lastDataRefresh } = props;
    const dispatch = useDispatch();

    const { showFilterOptions, setShowFilterOptions } = useContext(FilterContext);

    const {
        date_range: dateRangeParamValue,
        market: marketParamValue,
        prompt_type: promptTypeParamValue,
        strategic_theme: strategicThemeParamValue,
    } = useSelector((state: RootStateOrAny) => state.parameters);

    const marketDataAvailability = useMemo(() => ({
        start: MIN_SELECTABLE_DATE,
        end: lastDataRefresh || moment().subtract(1, "days").format("DD/MM/YYYY"),
    }), [lastDataRefresh]);

    const searchWithoutDomain = useMemo(() => {
        const p = new URLSearchParams(history.location.search);
        p.delete("domain");
        p.delete("funnel");
        return p.toString();
    }, [history.location.search]);

    const hasDateRange = useMemo(
        () => !!new URLSearchParams(history.location.search).get("date_range"),
        [history.location.search]
    );

    const { data } = useQuery({
        queryKey: ["geoReportingFilters", searchWithoutDomain],
        queryFn: () => getGeoReportingFilters(),
        enabled: hasDateRange,
        staleTime: 0,
        refetchOnWindowFocus: false,
    });

    const { market, prompt_type, strategic_theme } = data || {};

    // When filter options load, drop any selected values that are no longer valid options
    useEffect(() => {
        if (!data) return;

        const validateParam = (key: string, paramValue: string, options: string[]) => {
            if (!paramValue || paramValue === ALL_OPTION_NO_SPACE) return;
            const selected = paramValue.split(",").map((v) => v.trim()).filter(Boolean);
            const valid = selected.filter((v) => options.includes(v));
            if (valid.length !== selected.length) {
                dispatch(setSpecificParameter(key, valid.length > 0 ? valid.join(",") : ALL_OPTION_NO_SPACE));
            }
        };

        if (market) validateParam("market", marketParamValue, market);
        if (prompt_type) validateParam("prompt_type", promptTypeParamValue, prompt_type);
        if (strategic_theme) validateParam("strategic_theme", strategicThemeParamValue, strategic_theme);
    }, [data]);

    const [marketFilterValue, setMarketFilterValue] = useState<string>("");
    const [promptTypeFilterValue, setPromptTypeFilterValue] = useState<string>("");
    const [strategicThemeFilterValue, setStrategicThemeFilterValue] = useState<string>("");

    // Set defaults on mount if not already set or URL is missing date_range
    useEffect(() => {
        const urlDateRange = new URLSearchParams(history.location.search).get("date_range");
        if (!defaultFiltersSet("GEO Reporting") || !urlDateRange) {
            dispatch(setParameters(DEFAULT_GEO_REPORTING_PARAMETERS));
            currentDefaultFiltersSet("GEO Reporting");
        }
    }, []);

    // Normalize stale values from other pages back to ALL_OPTION_NO_SPACE in Redux
    useEffect(() => {
        if (isStaleParam(marketParamValue)) dispatch(setSpecificParameter("market", ALL_OPTION_NO_SPACE));
        if (isStaleParam(promptTypeParamValue)) dispatch(setSpecificParameter("prompt_type", ALL_OPTION_NO_SPACE));
        if (isStaleParam(strategicThemeParamValue)) dispatch(setSpecificParameter("strategic_theme", ALL_OPTION_NO_SPACE));
    }, [marketParamValue, promptTypeParamValue, strategicThemeParamValue]);

    // Compute display labels for each filter
    useEffect(() => {
        const selectedMarkets =
            market?.length === marketParamValue?.split(",")?.length
                ? "All"
                : marketParamValue?.includes(",")
                    ? `${marketParamValue?.split(",")?.length} markets selected`
                    : marketParamValue;
        setMarketFilterValue(selectedMarkets);
    }, [marketParamValue, market]);

    useEffect(() => {
        const selectedPrompts =
            prompt_type?.length === promptTypeParamValue?.split(",")?.length
                ? "All"
                : promptTypeParamValue?.includes(",")
                    ? `${promptTypeParamValue?.split(",")?.length} prompts selected`
                    : promptTypeParamValue;
        setPromptTypeFilterValue(selectedPrompts);
    }, [promptTypeParamValue, prompt_type]);

    useEffect(() => {
        const selectedStrategicThemes =
            strategic_theme?.length === strategicThemeParamValue?.split(",")?.length
                ? "All"
                : strategicThemeParamValue?.includes(",")
                    ? `${strategicThemeParamValue?.split(",")?.length} strategic themes selected`
                    : strategicThemeParamValue;
        setStrategicThemeFilterValue(selectedStrategicThemes);
    }, [strategicThemeParamValue, strategic_theme]);

    // Sync URL with Redux state; normalize any stale values before pushing
    useEffect(() => {
        if (!dateRangeParamValue) return;

        const normalizeParam = (val: string) =>
            !val || val.startsWith("All") || val === "undefined" ? ALL_OPTION_NO_SPACE : val;

        const params = new URLSearchParams();
        params.set("date_range", dateRangeParamValue);
        params.set("market", normalizeParam(marketParamValue));
        params.set("prompt_type", normalizeParam(promptTypeParamValue));
        params.set("strategic_theme", normalizeParam(strategicThemeParamValue));
        history.push({ search: `?${params.toString()}` });
    }, [dateRangeParamValue, marketParamValue, promptTypeParamValue, strategicThemeParamValue]);

    const showFilterOptionsFn = useCallback(
        (evt: React.MouseEvent<HTMLElement>) => {
            const optionsId = evt.currentTarget.dataset?.optionsUl;
            const optionsElement = optionsId ? document.getElementById(optionsId) : undefined;

            if (showFilterOptions && optionsElement?.classList.contains("showOptions")) {
                closeFilterOptions();
                closeMarketOptions();
                setShowFilterOptions(false);
            } else {
                closeFilterOptions();
                optionsElement?.classList.toggle("showOptions");
                setShowFilterOptions(!showFilterOptions);
            }
        },
        [showFilterOptions, setShowFilterOptions]
    );

    const handleMarketFilterSubmission = useCallback(() => {
        const selectedMarkets: string[] = [];
        document.querySelectorAll<HTMLInputElement>(
            `[type="checkbox"][data-filter-name="market"][data-options-type="single"]:checked`
        ).forEach((cb) => selectedMarkets.push(cb.value));

        closeFilterOptions();
        const value = market?.length === selectedMarkets.length ? "All" : selectedMarkets.join(",");
        dispatch(setSpecificParameter("market", value));
        eventTracking(MixpanelEvents.filter_change, { filter: "market", value, dashboard: "SEO", page: "GEO Reporting" });
    }, [market]);

    const handlePromptTypeFilterSubmission = useCallback(() => {
        const selectedPromptTypes: string[] = [];
        document.querySelectorAll<HTMLInputElement>(
            `[type="checkbox"][data-filter-name="prompt_type"][data-options-type="single"]:checked`
        ).forEach((cb) => selectedPromptTypes.push(cb.value));

        closeFilterOptions();
        const value = prompt_type?.length === selectedPromptTypes.length ? "All" : selectedPromptTypes.join(",");
        dispatch(setSpecificParameter("prompt_type", value));
        eventTracking(MixpanelEvents.filter_change, { filter: "prompt_type", value, dashboard: "SEO", page: "GEO Reporting" });
    }, [prompt_type]);

    const handleStrategicThemeFilterSubmission = useCallback(() => {
        const selectedStrategicThemes: string[] = [];
        document.querySelectorAll<HTMLInputElement>(
            `[type="checkbox"][data-filter-name="strategic_theme"][data-options-type="single"]:checked`
        ).forEach((cb) => selectedStrategicThemes.push(cb.value));

        closeFilterOptions();
        const value = strategic_theme?.length === selectedStrategicThemes.length ? "All" : selectedStrategicThemes.join(",");
        dispatch(setSpecificParameter("strategic_theme", value));
        eventTracking(MixpanelEvents.filter_change, { filter: "strategic_theme", value, dashboard: "SEO", page: "GEO Reporting" });
    }, [strategic_theme]);

    const handleFilterOptionClick = (evt: React.MouseEvent<HTMLElement>) => {
        const { filter: filterName, value: optionValue } = evt.currentTarget.dataset;
        if (filterName && optionValue) {
            dispatch(setSpecificParameter(filterName, optionValue));
            eventTracking(MixpanelEvents.filter_change, { filter: filterName, value: optionValue, dashboard: "SEO", page: "GEO Reporting" });
        }
        closeFilterOptions();
        setShowFilterOptions(false);
    };

    const onRenderOptions = ({ list, filter }: { list: string[]; filter: string }) => (
        <>
            {list?.map((item) => (
                <li key={item} data-filter={filter} onClick={handleFilterOptionClick} data-value={item}>
                    {item}
                </li>
            ))}
        </>
    );

    const resetFilters = useCallback(() => {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(DEFAULT_GEO_REPORTING_PARAMETERS)) {
            params.set(key, value);
        }
        history.push({ search: `?${params.toString()}` });
        window.location.reload();
    }, [history]);

    return (
        <FiltersLayout resetFilters={resetFilters} extraClass={"digital_filters"}>
            <>
                {!hideFilters && (
                    <>
                        <ErrorBoundary fallback={<ErrorMsg />}>
                            <DateFilter
                                onFilterClick={showFilterOptionsFn}
                                renderOptions={onRenderOptions}
                                value={dateRangeParamValue}
                                dateOptions={GEO_DATE_FILTER_OPTIONS}
                                page="GEO Reporting"
                                maxDate={""}
                                availableDates={marketDataAvailability}
                            />
                        </ErrorBoundary>

                        <ErrorBoundary fallback={<ErrorMsg />}>
                            <MultiSelectFilter
                                parentKey="market"
                                // @ts-ignore
                                filterList={market ?? []}
                                filterName="MARKET"
                                value={marketFilterValue}
                                parameterName={"market"}
                                parameterValue={marketParamValue}
                                onShowFilterOptions={showFilterOptionsFn}
                                handleFilterSubmission={handleMarketFilterSubmission}
                            />
                        </ErrorBoundary>

                        <ErrorBoundary fallback={<ErrorMsg />}>
                            <MultiSelectFilter
                                parentKey="prompt_type"
                                // @ts-ignore
                                filterList={prompt_type ?? []}
                                filterName="PROMPT TYPE"
                                value={promptTypeFilterValue}
                                parameterName={"prompt_type"}
                                parameterValue={promptTypeParamValue}
                                onShowFilterOptions={showFilterOptionsFn}
                                handleFilterSubmission={handlePromptTypeFilterSubmission}
                            />
                        </ErrorBoundary>

                        <ErrorBoundary fallback={<ErrorMsg />}>
                            <MultiSelectFilter
                                parentKey="strategic_theme"
                                // @ts-ignore
                                filterList={strategic_theme ?? []}
                                filterName="STRATEGIC THEME"
                                value={strategicThemeFilterValue}
                                parameterName={"strategic_theme"}
                                parameterValue={strategicThemeParamValue}
                                onShowFilterOptions={showFilterOptionsFn}
                                handleFilterSubmission={handleStrategicThemeFilterSubmission}
                            />
                        </ErrorBoundary>
                    </>
                )}

                {filterBtns.map((btn) => (
                    <FilterBtns id={btn.id} navigateTo={btn.navigateTo} name={btn.name} key={btn.id} showOnMobile={true} />
                ))}
            </>
        </FiltersLayout>
    );
});